import { BoundaryCreatorOutcome } from 'api-validations/aurora'
import { useEffect, useEffectEvent } from 'react'
import { useMapContext } from 'shared-ui/map'
import type { Stream } from 'shared-ui/operation-wide-data'
import { Geo } from 'utils'
import type { BoundarySource } from 'utils/boundary-autogen'

import {
  type BoundaryAutogenRequest,
  type BoundarySourceKey,
  takeBoundaryAutogen
} from '../lib/boundaryAutogenRequest'

const describeSource = (source: BoundarySource<Stream>) => ({
  season: source.season,
  task: source.task,
  dataSources: [...new Set(source.streams.map((stream) => stream.dataSource))],
  passCount: source.streams.length
})

const matchesKey = (key: BoundarySourceKey) => (source: BoundarySource<Stream>) =>
  source.season === key.season &&
  source.task === key.task &&
  source.streams.some((stream) => stream.dataSource === key.dataSource)

export const useAnswerBoundaryAutogen = ({
  request,
  sources,
  generate
}: {
  request: BoundaryAutogenRequest | null
  sources: BoundarySource<Stream>[] | undefined
  generate: (opts: {
    source: BoundarySource<Stream>
    onGenerated: () => void
    onError: (error: Error) => void
  }) => void
}) => {
  const { map } = useMapContext()

  const answer = useEffectEvent((pending: BoundaryAutogenRequest) => {
    if (sources === undefined || !takeBoundaryAutogen(pending)) return

    if (sources.length === 0) {
      pending.settle({ outcome: BoundaryCreatorOutcome.NoPasses })
      return
    }

    const source = pending.source === null ? sources[0] : sources.find(matchesKey(pending.source))
    if (source === undefined) {
      pending.settle({
        outcome: BoundaryCreatorOutcome.Failed,
        reason: 'This field has no group of passes matching that source.'
      })
      return
    }

    generate({
      source,
      onGenerated: () => {
        const { covered, holes, holeCount } = Geo.getAreas(map.drawTool.getFeatureCollection())
        pending.settle({
          outcome: BoundaryCreatorOutcome.Generated,
          source: describeSource(source),
          otherSources: sources.filter((other) => other !== source).map(describeSource),
          outlinedAcres: covered,
          innerAreas: { count: holeCount, acres: holes }
        })
      },
      onError: (error) =>
        pending.settle({ outcome: BoundaryCreatorOutcome.Failed, reason: error.message })
    })
  })

  const sourcesLoaded = sources !== undefined
  useEffect(() => {
    if (request === null || !sourcesLoaded) return
    answer(request)
  }, [request, sourcesLoaded])
}
